/**
 * Capacity Analysis Calculator - Pure functions for capacity and optimization calculations
 *
 * This module provides Level 5 pure functions for analyzing capacity, calculating
 * optimization scores, and determining interest impacts for bond adjustments.
 */

/**
 * Calculates interest multiplier for cascading interest impact estimation
 * Conservative estimate of how interest compounds over time
 * @param interestRate - Annual interest rate (as decimal)
 * @param years - Number of years
 * @returns Interest multiplier for capacity calculations
 * @pure Level 5 calculation - no side effects
 */
export function calculateInterestMultiplier(interestRate: number, years: number): number {
  // Conservative estimate: 1 + (rate * years * 0.5)
  // The 0.5 factor accounts for semi-annual compounding
  return 1 + interestRate * years * 0.5;
}

/**
 * Calculates maximum principal considering interest effects
 * @param drfCapacity - Available DRF capacity
 * @param interestMultiplier - Interest impact multiplier
 * @returns Maximum principal that can be added
 * @pure Level 5 calculation - no side effects
 */
export function calculateMaxPrincipalWithInterest(
  drfCapacity: number,
  interestMultiplier: number
): number {
  return Math.floor(drfCapacity / interestMultiplier);
}

/**
 * Calculates optimization score for period selection
 * Higher scores indicate better optimization opportunities
 * @param capacity - Available capacity at this period
 * @param periodIndex - Period index (0-based)
 * @param totalPeriods - Total number of periods
 * @returns Optimization score
 * @pure Level 5 calculation - no side effects
 */
export function calculateOptimizationScore(
  capacity: number,
  periodIndex: number,
  totalPeriods: number
): number {
  // Prefer earlier periods (higher weight for lower index)
  const earlinessWeight = 1 + (totalPeriods - periodIndex) / totalPeriods;
  return capacity * earlinessWeight;
}

/**
 * Calculates available revenue after obligations
 * @param revenue - Total revenue
 * @param existingDebt - Existing debt obligations
 * @param interestObligation - Interest obligations
 * @returns Available revenue for bonds
 * @pure Level 5 calculation - no side effects
 */
export function calculateAvailableRevenue(
  revenue: number,
  existingDebt: number,
  interestObligation: number
): number {
  return revenue - existingDebt - interestObligation;
}

/**
 * Calculates prorate ratio for interest obligations
 * @param actualDays - Actual period days (30/360 convention)
 * @param standardDays - Standard period days (typically 180)
 * @returns Prorate ratio for interest calculations
 * @pure Level 5 calculation - no side effects
 */
export function calculateProrateRatio(actualDays: number, standardDays: number): number {
  if (actualDays <= 0 || standardDays <= 0) {
    return 1.0; // Default to full period on invalid input
  }
  return actualDays / standardDays;
}

/**
 * Calculates revenue impact ratio
 * Shows what percentage of revenue is consumed by obligations
 * @param obligation - Interest or debt obligation
 * @param revenue - Total revenue available
 * @returns Impact ratio (0-1 scale)
 * @pure Level 5 calculation - no side effects
 */
export function calculateRevenueImpactRatio(obligation: number, revenue: number): number {
  if (revenue <= 0) return 0;
  return obligation / revenue;
}

/**
 * Calculates capacity adjusted for interest rate
 * Used in utilization optimization
 * @param capacity - Base capacity
 * @param interestRate - Interest rate (as decimal)
 * @returns Adjusted capacity
 * @pure Level 5 calculation - no side effects
 */
export function calculateInterestAdjustedCapacity(capacity: number, interestRate: number): number {
  return capacity / (1 + interestRate);
}

/**
 * Calculates compound interest capacity over multiple years
 * @param capacity - Base capacity
 * @param interestRate - Annual interest rate (as decimal)
 * @param years - Number of years
 * @returns Capacity adjusted for compound interest
 * @pure Level 5 calculation - no side effects
 */
export function calculateCompoundInterestCapacity(
  capacity: number,
  interestRate: number,
  years: number
): number {
  return capacity / (1 + interestRate) ** years;
}

/**
 * Calculates expected interest over a period
 * @param principal - Principal amount
 * @param interestRate - Interest rate (as decimal)
 * @param years - Number of years
 * @returns Expected total interest
 * @pure Level 5 calculation - no side effects
 */
export function calculateExpectedInterest(
  principal: number,
  interestRate: number,
  years: number
): number {
  return principal * interestRate * years;
}

/**
 * Calculates proportional distribution
 * @param itemCapacity - Individual item's capacity
 * @param totalCapacity - Total capacity of all items
 * @param amountToDistribute - Total amount to distribute
 * @returns Proportional amount for this item
 * @pure Level 5 calculation - no side effects
 */
export function calculateProportionalDistribution(
  itemCapacity: number,
  totalCapacity: number,
  amountToDistribute: number
): number {
  if (totalCapacity <= 0) return 0;
  const proportion = itemCapacity / totalCapacity;
  return proportion * amountToDistribute;
}
